import type { TFunctionReturn } from "@/modules/i18n/components/types"
import type { IconComponent } from "@jobteaser/spark/types/Icon"
import type { FunctionComponent, KeyboardEvent, MouseEvent, ReactNode } from "react"

import { clsx } from "clsx"
import { useEffect, useRef, useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Checkbox } from "@jobteaser/spark/components/Checkbox"
import { Cross } from "@jobteaser/spark/components/Icons/Cross"

import { Dropdown } from "@/modules/design/components/Dropdown/Dropdown"

import styles from "./MultiSelect.module.css"

export type SimpleSelectOption = {
  count?: number
  label: string
  value: string
}

type WithApplyLabelProps = {
  applyLabel: TFunctionReturn
  hasApplyButton: true
}

type WithoutApplyLabelProps = {
  applyLabel?: never
  hasApplyButton?: false
}

export type MultiSelectProps = {
  applyLabel?: TFunctionReturn
  className?: string
  containerClassname?: string
  icon?: IconComponent
  id: string
  label?: string | ReactNode
  labelClassName?: string
  linkCell?: ReactNode
  onChange?: (selectedValues: SimpleSelectOption[]) => void // usefull for multi select behind an apply modal
  onClose: (selectedValues: SimpleSelectOption[]) => void
  options: SimpleSelectOption[]
  placeholder: string
  value: SimpleSelectOption[]
} & (WithApplyLabelProps | WithoutApplyLabelProps)

export const MultiSelect: FunctionComponent<MultiSelectProps> = props => {
  const {
    applyLabel,
    className,
    containerClassname,
    hasApplyButton,
    icon: Icon,
    id,
    label,
    labelClassName,
    linkCell,
    onChange,
    onClose,
    options,
    placeholder,
    value,
  } = props
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValues, setSelectedValues] = useState<SimpleSelectOption[]>(value)

  const isFilled = selectedValues.length > 0

  useEffect(() => {
    setSelectedValues(value)
  }, [value])

  const handleClose = (): void => {
    triggerRef.current?.blur()
    setIsOpen(false)
    onClose(selectedValues)
  }

  const handleCheckboxChange = (checked: boolean, option: SimpleSelectOption): void => {
    if (checked) {
      setSelectedValues([...selectedValues, option])
      onChange?.([...selectedValues, option])
    } else {
      setSelectedValues(selectedValues.filter(selectedValue => selectedValue.value !== option.value))
      onChange?.(selectedValues.filter(selectedValue => selectedValue.value !== option.value))
    }
  }

  const handleClear = (): void => {
    setSelectedValues([])
    onClose([])
  }

  const computeDisplayableLabel = (): string | ReactNode => {
    if (isFilled) {
      if (selectedValues.length === 1) {
        return selectedValues[0].label
      }

      if (label) {
        return label
      }
    }

    return placeholder
  }

  return (
    <div
      className={clsx(className, styles.main, {
        [styles["dropdown--open"]]: isOpen,
        [styles["main--filled"]]: isFilled,
      })}
      data-testid={id}
      id={id}
    >
      <button
        className={clsx(styles.container, containerClassname)}
        onClick={event => {
          event.stopPropagation()
          if (isOpen) {
            handleClose()
          } else {
            setIsOpen(true)
          }
        }}
        onKeyDown={event => {
          if (event.key === "Space") {
            event.stopPropagation()
            if (isOpen) {
              handleClose()
            } else {
              setIsOpen(true)
            }
          }
        }}
        onTouchEnd={event => event.preventDefault()}
        onTouchStart={event => {
          event.stopPropagation()
          if (isOpen) {
            handleClose()
          } else {
            setIsOpen(true)
          }
        }}
        ref={triggerRef}
        type="button"
      >
        <div className={clsx(styles.label, labelClassName)}>
          {Icon && <Icon aria-hidden="true" width={20} />}
          <span>{computeDisplayableLabel()}</span>
        </div>
      </button>
      <button
        className={styles.indicator}
        id="multi-select-clear-options"
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          handleClear()
        }}
        onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === "Enter") {
            event.stopPropagation()
            handleClear()
          }
        }}
        type="button"
      >
        {selectedValues.length}
        <Cross height={12} width={12} />
      </button>
      {isOpen && (
        <Dropdown className={styles.dropdown} onClose={handleClose}>
          <ul id="multi-select-container-options" role="listbox">
            {options.map((option, index) => {
              return (
                <li
                  aria-selected={selectedValues.some(selectedValue => selectedValue.value === option.value)}
                  id={`multi-select-option-${index + 1}`}
                  key={option.value}
                  onClick={event => {
                    event.stopPropagation()
                  }}
                  onKeyDown={(event: KeyboardEvent<HTMLLIElement>) => {
                    const isLastOption = index === options.length - 1

                    const isPrevTab = event.shiftKey && event.key === "Tab"
                    const isNextTab = !event.shiftKey && event.key === "Tab"

                    if (isNextTab && !isPrevTab && isLastOption) {
                      handleClose()
                    }

                    if (event.key === "Enter") {
                      handleClose()
                    }
                  }}
                  role="option"
                >
                  <Checkbox
                    aria-selected={false}
                    checked={selectedValues.some(selectedValue => selectedValue.value === option.value)}
                    className={styles.checkbox}
                    name={option.value}
                    onChange={event => {
                      handleCheckboxChange(event.currentTarget.checked, option)
                    }}
                    onClick={event => event.stopPropagation()}
                    role="option"
                    secondaryLabel={`(${option.count || 0})`}
                    variant="dropdown-item"
                  >
                    {option.label}
                  </Checkbox>
                </li>
              )
            })}
            {linkCell}

            {hasApplyButton && (
              <li className={styles.apply}>
                <Button
                  onClick={handleClose}
                  onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                    if (event.key === "Enter") {
                      handleClose()
                    }
                  }}
                  type="button"
                >
                  {applyLabel}
                </Button>
              </li>
            )}
          </ul>
        </Dropdown>
      )}
    </div>
  )
}
